import { create } from 'zustand';

import { db } from '@/services/storage/db';
import { SeasonService } from '@/services/season-service';
import { daysUntilOpener, seasonStatus, SeasonStatus } from '@/utils/countdown-utils';
import type { Season, CreateSeasonInput, UpdateSeasonInput } from '@/types/season';
import { localTodayISO } from '@/utils/date-utils';

export { daysUntilOpener, seasonStatus };
export type { SeasonStatus };

const seasonService = new SeasonService(db);

/**
 * Deterministically elects which season should be primary when none is flagged.
 *
 * Mirrors the historical `primarySeason()` runtime fallback exactly so that the
 * persisted election equals what the fallback already displayed: prefer the
 * most recently created season that has not yet ended (no closer date, or a
 * closer date that is today or later). If every season has ended, fall back to
 * the most recently created season overall so an invariant primary still exists.
 *
 * Pure function — takes the reference date so callers control "today".
 */
export function electPrimarySeason(seasons: Season[], today: string): Season | undefined {
  if (seasons.length === 0) return undefined;
  const byNewestCreated = (a: Season, b: Season) => b.createdAt.localeCompare(a.createdAt);
  const notEnded = seasons.filter((s) => !s.closerDate || s.closerDate >= today);
  const pool = notEnded.length > 0 ? notEnded : seasons;
  return [...pool].sort(byNewestCreated)[0];
}

function todayISODate(): string {
  return localTodayISO();
}

interface SeasonStore {
  seasons: Season[];
  isLoading: boolean;
  /** True once the first loadSeasons() call has completed (success or error). */
  seasonsLoaded: boolean;
  error: string | null;

  loadSeasons: () => Promise<void>;
  getSeason: (id: string) => Season | undefined;
  getActiveSeasons: () => Season[];
  /** Returns the season with isPrimary=true, or falls back to the most recently created active season. */
  primarySeason: () => Season | undefined;
  addSeason: (data: CreateSeasonInput) => Promise<Season>;
  editSeason: (id: string, data: UpdateSeasonInput) => Promise<Season>;
  removeSeason: (id: string) => Promise<void>;
  setPrimary: (seasonId: string) => Promise<void>;
}

export const useSeasonStore = create<SeasonStore>((set, get) => ({
  seasons: [],
  isLoading: false,
  seasonsLoaded: false,
  error: null,

  loadSeasons: async () => {
    set({ isLoading: true, error: null });
    try {
      let seasons = await seasonService.getSeasons();
      // Self-heal: if seasons exist but none is flagged primary (legacy data
      // created before the single-primary invariant), elect one deterministically
      // and persist it through the single-primary write path. The elected season
      // equals what primarySeason()'s fallback would already display, so nothing
      // visibly changes on backfill — the flag simply becomes durable.
      if (seasons.length > 0 && !seasons.some((s) => s.isPrimary)) {
        const elected = electPrimarySeason(seasons, todayISODate());
        if (elected) {
          await db.setPrimarySeasons(elected.id);
          seasons = seasons.map((s) => ({ ...s, isPrimary: s.id === elected.id }));
        }
      }
      // Sort by opener date: nearest upcoming first, then future, then past
      const sorted = [...seasons].sort((a, b) => a.openerDate.localeCompare(b.openerDate));
      set({ seasons: sorted, isLoading: false, seasonsLoaded: true });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load seasons';
      console.error('SeasonStore.loadSeasons:', err);
      set({ error: message, isLoading: false, seasonsLoaded: true });
    }
  },

  getSeason: (id) => get().seasons.find((s) => s.id === id),

  getActiveSeasons: () => {
    const today = localTodayISO();
    return get().seasons.filter(
      (s) => s.openerDate <= today && (!s.closerDate || s.closerDate >= today),
    );
  },

  primarySeason: () => {
    const { seasons } = get();
    const flagged = seasons.find((s) => s.isPrimary);
    if (flagged) return flagged;
    // Fall back to the deterministic election (most recently created non-ended
    // season). Shared with loadSeasons' backfill so the displayed and persisted
    // primary always agree.
    return electPrimarySeason(seasons, todayISODate());
  },

  addSeason: async (data) => {
    try {
      // The first season — or any season created while no primary exists yet —
      // becomes primary so the single-primary invariant holds. If a primary
      // already exists, the new season is created non-primary and the existing
      // primary is left untouched.
      const hadPrimary = get().seasons.some((s) => s.isPrimary);
      const created = await seasonService.createSeason(data);
      let season = created;
      if (!hadPrimary) {
        // Route the election through the single-primary write path (reused, not
        // duplicated) so exactly one season ends up flagged.
        await db.setPrimarySeasons(created.id);
        season = { ...created, isPrimary: true };
      }
      set((state) => ({
        seasons: [...state.seasons, season].sort((a, b) =>
          a.openerDate.localeCompare(b.openerDate),
        ),
      }));
      return season;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create season';
      console.error('SeasonStore.addSeason:', err);
      set({ error: message });
      throw err;
    }
  },

  editSeason: async (id, data) => {
    try {
      const season = await seasonService.updateSeason(id, data);
      set((state) => ({
        seasons: state.seasons
          .map((s) => (s.id === id ? season : s))
          .sort((a, b) => a.openerDate.localeCompare(b.openerDate)),
      }));
      return season;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update season';
      console.error('SeasonStore.editSeason:', err);
      set({ error: message });
      throw err;
    }
  },

  removeSeason: async (id) => {
    try {
      const removed = get().seasons.find((s) => s.id === id);
      await seasonService.deleteSeason(id);
      const remaining = get().seasons.filter((s) => s.id !== id);

      // If the deleted season was the primary and seasons remain, promote
      // another deterministically so the single-primary invariant is preserved.
      // Deleting the last season leaves none, which is fine.
      let next = remaining;
      if (removed?.isPrimary && remaining.length > 0) {
        const promoted = electPrimarySeason(remaining, todayISODate());
        if (promoted) {
          await db.setPrimarySeasons(promoted.id);
          next = remaining.map((s) => ({ ...s, isPrimary: s.id === promoted.id }));
        }
      }
      set({ seasons: next });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete season';
      console.error('SeasonStore.removeSeason:', err);
      set({ error: message });
      throw err;
    }
  },

  setPrimary: async (seasonId) => {
    try {
      await db.setPrimarySeasons(seasonId);
      set((state) => ({
        seasons: state.seasons.map((s) => ({ ...s, isPrimary: s.id === seasonId })),
      }));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to set primary season';
      console.error('SeasonStore.setPrimary:', err);
      set({ error: message });
      throw err;
    }
  },
}));
